import React, { useState, useRef } from 'react';

const BACKEND = 'https://alphabioai-backend.onrender.com';

const T = {
  bg:        '#060d1e',
  surface:   '#0d1a2d',
  surfaceHi: '#132238',
  border:    '#1e3a5f',
  borderHi:  '#2d5a8e',
  text:      '#f0f6ff',
  textSub:   '#8ba5c8',
  textMute:  '#4a6a8a',
  emerald:   '#10b981',
  emeraldDim:'rgba(16,185,129,0.10)',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.10)',
  green:     '#22c55e',
  amber:     '#f59e0b',
  red:       '#ef4444',
  purple:    '#a78bfa',
};

const EXAMPLE_QUERIES = [
  "What cancer drugs have FDA decisions coming up?",
  "Show me high risk biotech this month",
  "Any Pfizer drugs I should know about?",
  "What should I be watching this week?",
  "Best risk/reward opportunities right now",
];

const CASUAL_WORDS = ['how are you', 'hello', 'hi ', 'hey ', 'good morning', 'good evening', 'thanks', 'thank you', 'what\'s up', 'sup '];

function isCasualQuery(query) {
  const q = query.toLowerCase();
  return CASUAL_WORDS.some(w => q.startsWith(w) || q === w.trim());
}

export default function NaturalSearch({ plan, isMobile, onTabChange }) {
  const [query, setQuery]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError]       = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef(null);

  const handleSearch = async (q) => {
    const searchQuery = (q || query).trim();
    if (!searchQuery || searchQuery.length < 3) return;

    setLoading(true);
    setError('');
    setResponse(null);
    setIsExpanded(true);

    try {
      const res = await fetch(`${BACKEND}/api/natural-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery, plan }),
      });
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      // For casual queries, clear results so we only show the answer
      if (isCasualQuery(searchQuery)) {
        data.results = [];
      }
      setResponse(data);
    } catch (e) {
      setError("Hmm, something went wrong on our end. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (example) => {
    setQuery(example);
    handleSearch(example);
    inputRef.current?.blur();
  };

  const handleClose = () => {
    setIsExpanded(false);
    setResponse(null);
    setError('');
    setQuery('');
  };

  return (
    <div style={{ marginBottom: isExpanded ? '28px' : '20px', transition: 'margin 0.2s' }}>

      {/* Search input */}
      <div style={{
        position: 'relative',
        background: isExpanded ? T.emeraldDim : 'transparent',
        border: `1px solid ${isExpanded ? 'rgba(16,185,129,0.25)' : T.border}`,
        borderRadius: isExpanded ? '12px 12px 0 0' : '10px',
        transition: 'all 0.2s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: isMobile ? '10px 14px' : '12px 18px', gap: '12px' }}>
          <div style={{
            flexShrink: 0, width: '32px', height: '32px',
            background: `linear-gradient(135deg, ${T.emerald}, ${T.blue})`,
            borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '15px',
          }}>✦</div>

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); if (e.key === 'Escape') handleClose(); }}
            onFocus={() => !isExpanded && setIsExpanded(true)}
            placeholder={isMobile ? "Ask anything about biotech..." : "Ask anything... e.g. 'What cancer drugs have FDA decisions coming up?'"}
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: T.text, fontSize: isMobile ? '14px' : '15px',
              fontFamily: "'Inter', system-ui, sans-serif",
            }}
          />

          {query && (
            <button
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              style={{ background: 'none', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: '16px', padding: '4px', flexShrink: 0 }}>
              ✕
            </button>
          )}

          <button
            onClick={() => handleSearch()}
            disabled={loading || !query.trim()}
            style={{
              flexShrink: 0,
              background: query.trim() ? T.emerald : T.surfaceHi,
              border: 'none', borderRadius: '7px',
              color: query.trim() ? 'white' : T.textMute,
              padding: '7px 16px', fontSize: '13px', fontWeight: '600',
              cursor: query.trim() ? 'pointer' : 'not-allowed',
              transition: 'all 0.15s', fontFamily: 'inherit',
            }}>
            {loading ? '...' : 'Ask'}
          </button>
        </div>

        {/* Example queries */}
        {isExpanded && !response && !loading && !error && (
          <div style={{ padding: isMobile ? '0 14px 14px' : '0 18px 16px', borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: '11px', color: T.textMute, fontWeight: '700', letterSpacing: '0.5px', marginBottom: '10px', marginTop: '12px' }}>
              TRY ASKING
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {EXAMPLE_QUERIES.map((ex, i) => (
                <button
                  key={i}
                  onClick={() => handleExampleClick(ex)}
                  style={{
                    background: T.surfaceHi, border: `1px solid ${T.border}`,
                    borderRadius: '20px', padding: '5px 12px',
                    fontSize: '12px', color: T.textSub, cursor: 'pointer',
                    fontFamily: 'inherit', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.emerald; e.currentTarget.style.color = T.text; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSub; }}>
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Response panel */}
      {isExpanded && (loading || response || error) && (
        <div style={{
          background: T.emeraldDim,
          border: `1px solid rgba(16,185,129,0.2)`,
          borderTop: 'none',
          borderRadius: '0 0 12px 12px',
          padding: isMobile ? '16px 14px' : '20px 22px',
        }}>

          {/* Loading */}
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    backgroundColor: T.emerald, opacity: 0.7,
                    animation: `dotPulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
              </div>
              <span style={{ fontSize: '14px', color: T.textSub, fontStyle: 'italic' }}>
                Thinking through your question...
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ fontSize: '14px', color: T.amber, lineHeight: '1.6' }}>{error}</div>
          )}

          {/* AI Response */}
          {response && !loading && (
            <div>
              <div style={{ display: 'flex', gap: '12px', marginBottom: response.results?.length ? '20px' : '0' }}>
                <div style={{
                  flexShrink: 0, width: '28px', height: '28px',
                  background: `linear-gradient(135deg, ${T.emerald}, ${T.blue})`,
                  borderRadius: '7px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', marginTop: '2px',
                }}>✦</div>
                <div>
                  <p style={{ margin: 0, fontSize: '15px', lineHeight: '1.75', color: '#c8deff', fontWeight: '400' }}>
                    {response.answer}
                  </p>
                  {response.results?.length > 0 && (
                    <div style={{ fontSize: '12px', color: T.textMute, marginTop: '8px' }}>
                      Found {response.results.length} relevant {response.results.length === 1 ? 'item' : 'items'} —
                      <span
                        onClick={() => onTabChange && onTabChange(response.suggested_tab || 'fda')}
                        style={{ color: T.emerald, cursor: 'pointer', marginLeft: '4px' }}>
                        view all in {response.suggested_tab === 'calendar' ? 'FDA Calendar' : response.suggested_tab === 'trials' ? 'Clinical Trials' : 'FDA Drugs'} →
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Result cards — only shown for real biotech queries */}
              {response.results?.length > 0 && (
                <div style={{ display: 'grid', gap: '6px' }}>
                  {response.results.slice(0, 5).map((item, i) => (
                    <ResultCard key={i} item={item} />
                  ))}
                </div>
              )}

              <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: `1px solid ${T.border}`, fontSize: '12px', color: '#5a7a9a', letterSpacing: '0.2px' }}>
                Not financial advice — always do your own research before making investment decisions.
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes dotPulse {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function ResultCard({ item }) {
  const getRiskColor = (r) => r === 'Low' ? T.green : r === 'High' ? T.red : T.amber;
  const getRiskBg    = (r) => r === 'Low' ? 'rgba(34,197,94,0.08)' : r === 'High' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)';
  const isCalendar   = item.type === 'calendar';
  const isPDUFA      = isCalendar && item.status === 'pending';

  return (
    <div style={{
      backgroundColor: T.surface,
      border: `1px solid ${T.border}`,
      borderLeft: `2px solid ${isCalendar ? (isPDUFA ? T.red : T.purple) : getRiskColor(item.risk_level)}`,
      borderRadius: '7px',
      padding: '10px 14px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: T.text, marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.name}
        </div>
        <div style={{ fontSize: '12px', color: T.textSub }}>
          {item.company}
          {item.date && <span style={{ marginLeft: '8px', color: T.textMute }}>· {item.date}</span>}
        </div>
      </div>
      <div style={{ flexShrink: 0 }}>
        {item.risk_level && (
          <span style={{ fontSize: '11px', fontWeight: '700', color: getRiskColor(item.risk_level), backgroundColor: getRiskBg(item.risk_level), padding: '3px 9px', borderRadius: '4px' }}>
            {item.risk_level}
          </span>
        )}
        {isCalendar && item.days_until !== undefined && (
          <span style={{
            fontSize: '11px', fontWeight: '700',
            color: item.days_until <= 7 ? T.red : item.days_until <= 30 ? T.amber : T.blue,
            backgroundColor: item.days_until <= 7 ? 'rgba(239,68,68,0.08)' : item.days_until <= 30 ? 'rgba(245,158,11,0.08)' : 'rgba(59,130,246,0.08)',
            padding: '3px 9px', borderRadius: '4px',
          }}>
            {item.days_until === 0 ? 'TODAY' : item.days_until === 1 ? 'Tomorrow' : `${item.days_until}d`}
          </span>
        )}
      </div>
    </div>
  );
}